import { useQuery } from "@tanstack/react-query";
import { errorText } from "@common/api";
import { admin, queryKeys } from "../../api/salvagers";

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

const relative = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

/** "3 hours ago", "yesterday", "just now". */
export function timeAgo(iso: string, now: number = Date.now()): string {
  const seconds = (new Date(iso).getTime() - now) / 1000;
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return relative.format(Math.round(seconds / size), unit);
  }
  return "just now";
}

function When({ iso, never }: { iso: string | null; never: string }) {
  return iso ? (
    <time dateTime={iso} title={new Date(iso).toLocaleString()}>
      {timeAgo(iso)} <span className="muted small">{new Date(iso).toLocaleDateString()}</span>
    </time>
  ) : (
    <span className="muted">{never}</span>
  );
}

/** This app's Lambda functions: when each was last uploaded and last ran. */
export default function Lambdas() {
  const lambdas = useQuery({ queryKey: queryKeys.lambdas, queryFn: admin.lambdas });
  return (
    <section>
      <h2>Lambdas</h2>
      <p className="muted">
        Last upload is Lambda's last-modified time, which a configuration change also moves. Last run is the newest
        event in the function's log group.
      </p>
      {lambdas.error && (
        <p className="notice error">{errorText(lambdas.error, "Could not read the Lambda functions.")}</p>
      )}
      {lambdas.data && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Function</th>
                <th>Runtime</th>
                <th>Last upload</th>
                <th>Last run</th>
              </tr>
            </thead>
            <tbody>
              {lambdas.data.map((lambda) => (
                <tr key={lambda.name}>
                  <td>
                    <strong>{lambda.name}</strong>
                  </td>
                  <td>{lambda.runtime ?? <span className="muted">Container image</span>}</td>
                  <td>
                    <When iso={lambda.last_modified} never="-" />
                  </td>
                  <td>
                    <When iso={lambda.last_invoked_at} never="No logs" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
